import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2, XCircle, Star, Download, Shield, Zap, Clock, Users,
  Trophy, ChevronDown, Smartphone, Globe, FileText, MessageCircle,
  Award, Lock, ArrowRight, Check, AlertCircle, Sparkles
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/* ═══════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════ */
const CHECKOUT = "https://digitalenergygroup.mychariow.shop/candidatincontournable/checkout";
const PRODUCT  = "https://digitalenergygroup.mychariow.shop/candidatincontournable";

const IMGS = {
  overview: "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-23/prod_agent_8ad743db-79ed-4196-81d3-0fcae9193427/Capture%20d%27%C3%A9cran%202026-05-21%20194023_9b72cfa1ef1a44d3a764a5a0285e8adf.png",
  folders:  "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-23/prod_agent_8ad743db-79ed-4196-81d3-0fcae9193427/Capture%20d%27%C3%A9cran%202026-05-21%20194217_8e12774d2d2744d58bb5e0c92102945c.png",
  letters:  "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-23/prod_agent_8ad743db-79ed-4196-81d3-0fcae9193427/Capture%20d%27%C3%A9cran%202026-05-21%20195043_ef66c6c9a24d47c8bdab4c7ad2e1abfe.png",
  cvguide:  "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-23/prod_agent_8ad743db-79ed-4196-81d3-0fcae9193427/Capture%20d%27%C3%A9cran%202026-05-21%20195301_523e13bbaed644f09cfff1687754a786.png",
  letters2: "https://skyagent-artifacts.skywork.ai/router/agent/2026-05-23/prod_agent_8ad743db-79ed-4196-81d3-0fcae9193427/Capture%20d%27%C3%A9cran%202026-05-21%20201408_477dfa159ead457e878cb6245d2268ac.png",
  structure:"https://skyagent-artifacts.skywork.ai/router/agent/2026-05-23/prod_agent_8ad743db-79ed-4196-81d3-0fcae9193427/Capture%20d%27%C3%A9cran%202026-05-21%20201636_329ad24f2b7e4ebf935eb9a55c896e30.png",
};

/* ─── Design tokens ─── */
const D = {
  bg0:  "#030303",
  bg1:  "#050505",
  bg2:  "#080808",
  bg3:  "#0d0d0d",
  bg4:  "#111111",
  card: "rgba(255,255,255,0.04)",
  cardBorderGold: "rgba(244,180,0,0.18)",
  cardBorderOrange: "rgba(255,122,0,0.22)",
  gold: "#F4B400",
  orange: "#FF7A00",
  glow: "#FFC857",
  textPrimary: "#FFFFFF",
  textSecondary: "#C9C9C9",
  textMuted: "#666666",
  divider: "rgba(255,255,255,0.06)",
};

/* ═══════════════════════════════════════════════════════════
   ANIMATIONS
═══════════════════════════════════════════════════════════ */
const fadeUp = {
  hidden:  { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.11 } },
};
const fadeIn = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

/* ═══════════════════════════════════════════════════════════
   COUNTDOWN  (5 h, auto-restart)
═══════════════════════════════════════════════════════════ */
function useCountdown() {
  const INIT = { h: 5, m: 0, s: 0 };
  const [time, setTime] = useState(INIT);
  useEffect(() => {
    const id = setInterval(() => {
      setTime(p => {
        const { h, m, s } = p;
        if (s > 0) return { h, m, s: s - 1 };
        if (m > 0) return { h, m: m - 1, s: 59 };
        if (h > 0) return { h: h - 1, m: 59, s: 59 };
        return { ...INIT };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return time;
}
const pad = (n: number) => String(n).padStart(2, "0");

/* ═══════════════════════════════════════════════════════════
   CTA BUTTON  (pulsing gold/orange)
═══════════════════════════════════════════════════════════ */
function CTAButton({ label, className = "" }: { label: string; className?: string }) {
  return (
    <motion.a
      href={CHECKOUT} target="_blank" rel="noopener noreferrer"
      whileHover={{ scale: 1.05, boxShadow: "0 0 52px 14px rgba(255,122,0,0.55)" }}
      whileTap={{ scale: 0.97 }}
      animate={{ boxShadow: ["0 8px 30px rgba(255,122,0,0.35)","0 8px 48px rgba(255,183,3,0.55)","0 8px 30px rgba(255,122,0,0.35)"] }}
      transition={{ boxShadow: { duration: 2.5, repeat: Infinity, ease: "easeInOut" } }}
      className={`inline-flex items-center justify-center gap-2 font-extrabold text-white rounded-2xl px-8 py-4 text-lg cursor-pointer select-none ${className}`}
      style={{ background: "linear-gradient(135deg,#FF7A00 0%,#FFB703 100%)", minHeight: 56, letterSpacing: "-0.01em" }}
    >{label}</motion.a>
  );
}

/* ═══════════════════════════════════════════════════════════
   CONVERSION TOAST  (floating notifications)
═══════════════════════════════════════════════════════════ */
const TOASTS = [
  { icon: "🔥", text: "Nouvelle commande confirmée",         sub: "Il y a 1 min · Dakar" },
  { icon: "⚡", text: "Un utilisateur vient d'obtenir le pack", sub: "Il y a 2 min · Abidjan" },
  { icon: "💼", text: "Téléchargement instantané activé",    sub: "Il y a 3 min · Lomé" },
  { icon: "🎯", text: "Offre promo encore disponible",       sub: "Plus que quelques heures" },
  { icon: "📩", text: "Nouveau client enregistré",           sub: "Il y a 4 min · Bamako" },
  { icon: "🚀", text: "Accès immédiat au pack débloqué",     sub: "Il y a 1 min · Ouagadougou" },
  { icon: "💳", text: "Paiement confirmé avec succès",       sub: "Il y a 2 min · Cotonou" },
  { icon: "📂", text: "Pack téléchargé avec succès",         sub: "Il y a 5 min · Conakry" },
  { icon: "⏳", text: "Plus que quelques h au prix réduit",  sub: "Offre limitée" },
  { icon: "🌟", text: "Ibrahim vient d'obtenir son accès",   sub: "Il y a 3 min · Lagos" },
];

function ConversionToast() {
  const [visible, setVisible] = useState(false);
  const [cur, setCur] = useState(0);
  useEffect(() => {
    let show: ReturnType<typeof setTimeout>;
    let hide: ReturnType<typeof setTimeout>;
    const cycle = (i: number) => {
      show = setTimeout(() => {
        setCur(i); setVisible(true);
        hide = setTimeout(() => {
          setVisible(false);
          cycle((i + 1) % TOASTS.length);
        }, 4500);
      }, Math.random() * 5000 + 9000);
    };
    cycle(Math.floor(Math.random() * TOASTS.length));
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const msg = TOASTS[cur];
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ x: -110, opacity: 0, scale: 0.88 }}
          animate={{ x: 0, opacity: 1, scale: 1 }}
          exit={{ x: -110, opacity: 0, scale: 0.88 }}
          transition={{ type: "spring", stiffness: 420, damping: 32 }}
          className="fixed bottom-24 left-4 z-50 sm:bottom-8 flex items-center gap-3 rounded-2xl px-4 py-3 max-w-[280px]"
          style={{ background: "rgba(5,5,5,0.97)", border: `1px solid ${D.cardBorderGold}`, boxShadow: `0 8px 36px rgba(0,0,0,0.7), 0 0 24px rgba(244,180,0,0.12)` }}
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-lg shrink-0"
            style={{ background: "rgba(255,122,0,0.14)", border: `1px solid rgba(255,122,0,0.28)` }}>
            {msg.icon}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-bold leading-tight" style={{ color: D.textPrimary }}>{msg.text}</p>
            <p className="text-xs mt-0.5" style={{ color: D.textMuted }}>{msg.sub}</p>
          </div>
          <div className="w-2 h-2 rounded-full shrink-0 animate-pulse" style={{ background: "#22c55e" }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════════════════════════════════════════════════
   NAV BAR (tagline + bouton Découvrir le PACK)
═══════════════════════════════════════════════════════════ */
function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <nav
      className="sticky top-0 left-0 right-0 z-40 w-full px-4 py-3 flex items-center justify-between gap-4 transition-all"
      style={{
        background: scrolled ? "rgba(5,5,5,0.98)" : "rgba(5,5,5,0.95)",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        borderBottom: `1px solid ${scrolled ? "rgba(244,180,0,0.18)" : "rgba(255,255,255,0.06)"}`,
        boxShadow: scrolled ? "0 4px 30px rgba(0,0,0,0.7)" : "none",
      }}
    >
      {/* Tagline */}
      <p
        className="text-xs sm:text-sm font-bold leading-tight max-w-xs sm:max-w-md hidden sm:block"
        style={{ color: "#C9C9C9", letterSpacing: "0.01em" }}
      >
        <span className="text-white font-extrabold">Emplois, stages, bourses, opportunités…</span>{" "}
        tout commence par une candidature convaincante.
      </p>
      {/* Mobile: logo */}
      <p className="text-xs font-black sm:hidden" style={{ color: "#FF7A00" }}>
        CANDIDAT INCONTOURNABLE™
      </p>

      {/* CTA button */}
      <motion.a
        href={PRODUCT}
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.05, boxShadow: "0 0 28px 6px rgba(255,122,0,0.45)" }}
        whileTap={{ scale: 0.97 }}
        animate={{ boxShadow: ["0 4px 18px rgba(255,122,0,0.3)","0 4px 28px rgba(255,183,3,0.5)","0 4px 18px rgba(255,122,0,0.3)"] }}
        transition={{ boxShadow: { duration: 2.5, repeat: Infinity, ease: "easeInOut" } }}
        className="shrink-0 inline-flex items-center gap-2 font-extrabold text-white rounded-xl px-4 py-2.5 text-sm cursor-pointer whitespace-nowrap"
        style={{ background: "linear-gradient(135deg,#FF7A00,#FFB703)", letterSpacing: "-0.01em" }}
      >
        🚀 Découvrir le PACK
      </motion.a>
    </nav>
  );
}

/* ═══════════════════════════════════════════════════════════
   SALES POPUP  — intervalles aléatoires, compact, premium
═══════════════════════════════════════════════════════════ */
const SALES_NOTIFS = [
  { name:"Samuel K.",  city:"Lomé, Togo",          action:"vient de télécharger le pack 📩",           time:"Il y a 5 min" },
  { name:"Grâce A.",   city:"Abidjan, Côte d'Ivoire",action:"vient d'acheter le pack ✅",              time:"Il y a 12 min" },
  { name:"Kevin M.",   city:"Dakar, Sénégal",       action:"a reçu son accès immédiat ⚡",             time:"Il y a 20 min" },
  { name:"Sarah T.",   city:"Cotonou, Bénin",       action:"a obtenu le bonus CV 🎁",                  time:"Il y a 30 min" },
  { name:"Amina D.",   city:"Lomé, Togo",           action:"vient de télécharger le pack 📩",           time:"Il y a 1 heure" },
  { name:"Moussa B.",  city:"Bamako, Mali",          action:"a activé son accès au pack 🚀",            time:"Il y a 3 heures" },
  { name:"Carine T.",  city:"Douala, Cameroun",      action:"a confirmé son paiement 💳",               time:"Hier" },
  { name:"Ibrahim S.", city:"Conakry, Guinée",       action:"vient de rejoindre le pack 🎯",            time:"Il y a 1 jour" },
  { name:"Fatou N.",   city:"Dakar, Sénégal",        action:"vient de télécharger le pack 📩",          time:"Il y a 8 min" },
  { name:"Jean-Paul M.",city:"Yaoundé, Cameroun",    action:"a obtenu son accès instantané ⚡",         time:"Il y a 25 min" },
  { name:"Aicha K.",   city:"Adidogomé, Togo",       action:"vient d'acheter le pack ✅",               time:"Il y a 2 heures" },
  { name:"Patrick O.", city:"Libreville, Gabon",     action:"a reçu les modèles CV 📄",                time:"Il y a 45 min" },
  { name:"Mariama S.", city:"Dapaong, Togo",         action:"vient de télécharger le pack 📩",          time:"Il y a 15 min" },
  { name:"Edouard B.", city:"Ouagadougou, Burkina",  action:"a activé son accès au pack 🚀",            time:"Il y a 1 heure" },
  { name:"Jocelyne A.",city:"Abidjan, Côte d'Ivoire",action:"vient d'acheter le pack ✅",               time:"Il y a 35 min" },
  { name:"Kwame N.",   city:"Accra, Ghana",          action:"a obtenu le bonus CV 🎁",                  time:"Il y a 3 heures" },
  { name:"Céleste M.", city:"Lomé, Togo",            action:"a confirmé son paiement 💳",               time:"Hier" },
  { name:"Amine R.",   city:"Casablanca, Maroc",     action:"vient d'obtenir son accès immédiat ⚡",    time:"Il y a 6 min" },
  { name:"Diane F.",   city:"Kigali, Rwanda",        action:"vient de télécharger le pack 📩",          time:"Il y a 50 min" },
  { name:"Bertrand K.",city:"Cotonou, Bénin",        action:"a rejoint le programme 🚀",                time:"Il y a 2 heures" },
];

const RANDOM_DELAYS = [5000, 10000, 15000, 20000, 30000, 45000];

function SalesPopup() {
  const [idx, setIdx]       = useState(() => Math.floor(Math.random() * SALES_NOTIFS.length));
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cycleTimer: ReturnType<typeof setTimeout>;

    const schedule = () => {
      const delay = RANDOM_DELAYS[Math.floor(Math.random() * RANDOM_DELAYS.length)];
      cycleTimer = setTimeout(() => {
        setIdx(i => (i + 1) % SALES_NOTIFS.length);
        setVisible(true);
        // Visible pendant 5.5s puis disparaît
        setTimeout(() => {
          setVisible(false);
          schedule(); // re-schedule with new random delay
        }, 5500);
      }, delay);
    };

    // Premier affichage après 5s
    cycleTimer = setTimeout(() => {
      setVisible(true);
      setTimeout(() => {
        setVisible(false);
        schedule();
      }, 5500);
    }, 5000);

    return () => clearTimeout(cycleTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const n = SALES_NOTIFS[idx];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key={idx}
          initial={{ x: -30, y: 10, opacity: 0, scale: 0.95 }}
          animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          exit={{ x: -20, opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 320, damping: 28, duration: 0.4 }}
          className="fixed z-50 flex items-center gap-3"
          style={{
            bottom: 80,
            left: 16,
            background: "rgba(12,20,44,0.97)",
            color: "white",
            padding: "10px 13px",
            borderRadius: "14px",
            boxShadow: "0 8px 28px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,200,150,0.15)",
            border: "1px solid rgba(0,200,150,0.4)",
            maxWidth: 260,
            width: "calc(100vw - 32px)",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            backdropFilter: "blur(12px)",
          }}
        >
          {/* Icon */}
          <div style={{
            background: "linear-gradient(135deg,#18c964,#00a878)",
            width: 36, height: 36, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, flexShrink: 0,
            boxShadow: "0 0 12px rgba(24,201,100,0.4)",
          }}>🔔</div>

          {/* Content */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ fontWeight: 700, fontSize: 12, color: "#fff", lineHeight: 1.35, margin: 0 }}>
              {n.name} <span style={{ color: "#94a3b8", fontWeight: 400 }}>· {n.city}</span>
            </p>
            <p style={{ color: "#a5b4fc", fontSize: 11, lineHeight: 1.35, margin: "2px 0" }}>{n.action}</p>
            <p style={{ color: "#64748b", fontSize: 10, margin: 0 }}>{n.time}</p>
          </div>

          {/* Live dot */}
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", flexShrink: 0, boxShadow: "0 0 6px #22c55e" }} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════════════════════════════════════════════════
   1. URGENCY BAR
═══════════════════════════════════════════════════════════ */
function UrgencyBar() {
  const { h, m, s } = useCountdown();
  return (
    <div
      className="w-full relative overflow-hidden z-50"
      style={{ background: "linear-gradient(90deg,#c0392b,#96281b,#c0392b)", backgroundSize: "200% 100%", animation: "shimmerBg 4s linear infinite" }}
    >
      <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-5 py-2 sm:py-2.5 px-3 sm:px-6 text-white text-center">

        {/* Promo text — compact on mobile */}
        <span className="flex items-center justify-center flex-wrap gap-x-1.5 gap-y-0 text-xs sm:text-sm font-semibold leading-snug">
          <span className="animate-bounce text-sm">⚡</span>
          <span className="hidden xs:inline">Dernière chance —</span>
          <strong className="font-black text-yellow-200 whitespace-nowrap text-xs sm:text-sm">
            CANDIDAT INCONTOURNABLE™
          </strong>
          <span className="hidden sm:inline text-xs opacity-80">à prix réduit</span>
          <span className="font-black px-2 py-0.5 rounded-full text-xs shrink-0"
            style={{ background: "#F4B400", color: "#000" }}>PROMO</span>
        </span>

        {/* Separator — desktop only */}
        <span className="hidden sm:block opacity-30">|</span>

        {/* Countdown */}
        <span className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold">
          <Clock className="w-3 h-3 sm:w-4 sm:h-4 shrink-0 opacity-80" />
          <span className="opacity-85 text-xs sm:text-sm">
            <span className="hidden sm:inline">L'offre expire dans : </span>
            <span className="sm:hidden">Expire : </span>
          </span>
          {[{ v: h, l: "H" }, { v: m, l: "M" }, { v: s, l: "S" }].map((t, i) => (
            <span key={i} className="flex items-baseline gap-0">
              <span
                className="font-mono font-black text-xs sm:text-sm tracking-wider rounded px-1.5 sm:px-2 py-0.5 inline-block"
                style={{ background: "rgba(0,0,0,0.4)", color: "#FFB703", textShadow: "0 0 8px rgba(255,183,3,0.8)", minWidth: "1.75rem", textAlign: "center" }}
              >
                {pad(t.v)}
              </span>
              <span className="text-xs opacity-60 ml-0.5">{t.l}</span>
              {i < 2 && <span className="opacity-50 mx-0.5 text-xs">:</span>}
            </span>
          ))}
        </span>

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   2. HERO SECTION
═══════════════════════════════════════════════════════════ */
function HeroSection() {
  return (
    <section className="relative overflow-hidden pt-10 pb-16 px-4"
      style={{ background: `linear-gradient(160deg,${D.bg0} 0%,${D.bg1} 40%,${D.bg2} 80%,${D.bg0} 100%)` }}>
      {/* Ambient glow orbs */}
      <div className="absolute top-10 left-1/3 w-[600px] h-[600px] rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(255,122,0,0.10)" }} />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(244,180,0,0.08)" }} />
      <div className="absolute top-1/2 left-0 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: "rgba(255,122,0,0.06)" }} />

      <div className="max-w-6xl mx-auto">
        {/* Badges */}
        <motion.div className="flex flex-wrap justify-center gap-3 mb-8"
          initial="hidden" animate="visible" variants={stagger}>
          {[{ icon: "⭐", t: "4.9/5 — Très bien noté" },{ icon: "🔥", t: "Offre limitée" },{ icon: "⚡", t: "Accès immédiat" }].map(b => (
            <motion.span key={b.t} variants={fadeIn}
              className="flex items-center gap-1.5 text-xs font-bold px-3.5 py-1.5 rounded-full"
              style={{ background: "rgba(255,122,0,0.12)", border: `1px solid ${D.cardBorderOrange}`, color: D.orange }}>
              {b.icon} {b.t}
            </motion.span>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left copy */}
          <motion.div className="text-center lg:text-left" initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: D.orange }}>
              🚀 Emplois · Stages · Bourses · Opportunités
            </motion.p>

            <motion.h1 variants={fadeUp}
              className="font-black leading-[1.06] mb-5"
              style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", letterSpacing: "-0.03em", color: D.textPrimary, fontSize: "clamp(2rem, 9vw, 4.5rem)" }}>
              Devenez le{" "}
              <span style={{ color: D.orange }}>Candidat</span>
              <br />
              <span style={{ background: `linear-gradient(90deg,${D.gold},${D.orange},${D.glow})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Incontournable™
              </span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg sm:text-xl mb-3 leading-relaxed" style={{ color: D.textSecondary }}>
              Chaque offre reçoit <strong className="text-white">des centaines de candidatures</strong>.
              Les recruteurs passent <strong className="text-white">moins de 6 secondes</strong> sur chaque CV.
            </motion.p>
            <motion.p variants={fadeUp} className="text-base mb-8 leading-relaxed" style={{ color: "#888" }}>
              ⚡ Créez une candidature <strong className="text-white">professionnelle, crédible et difficile à ignorer</strong> en quelques minutes.
            </motion.p>

            {/* Trust chips */}
            <motion.div variants={fadeUp} className="flex flex-wrap justify-center lg:justify-start gap-2 mb-8">
              {["✅ Accès immédiat","✅ Paiement unique","✅ Compatible mobile & PC","✅ Débutants & pros"].map(t => (
                <span key={t} className="text-sm font-medium px-3 py-1 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: D.textSecondary }}>{t}</span>
              ))}
            </motion.div>

            {/* CTA */}
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <CTAButton label="🚀 JE PROFITE DE L'OFFRE" className="w-full sm:w-auto text-xl px-10 py-5" />
              <div className="text-center">
                <div className="text-sm line-through font-mono" style={{ color: D.textMuted }}>55 000 FCFA</div>
                <div className="font-black text-2xl" style={{ color: D.orange }}>3 000 FCFA <span className="text-base" style={{ color: D.textMuted }}>≈ 5 USD</span></div>
              </div>
            </motion.div>

            <motion.p variants={fadeUp} className="mt-4 text-xs flex items-center justify-center lg:justify-start gap-1" style={{ color: D.textMuted }}>
              <Lock className="w-3 h-3" /> Paiement 100% sécurisé · Accès immédiat après commande
            </motion.p>
          </motion.div>

          {/* Right mockup */}
          <motion.div className="relative flex justify-center w-full"
            initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22,1,0.36,1] }}>
            <div className="absolute inset-0 blur-2xl rounded-3xl pointer-events-none" style={{ background: "radial-gradient(circle,rgba(255,122,0,0.22),transparent 70%)" }} />
            <div className="relative rounded-3xl overflow-hidden w-full max-w-full sm:max-w-md"
              style={{ border: `2px solid ${D.cardBorderOrange}`, boxShadow: "0 20px 60px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.04)" }}>
              <img src={IMGS.overview} alt="Pack Candidat Incontournable" className="w-full block object-cover" />
              <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(to bottom,transparent 60%,rgba(0,0,0,0.5))" }} />
              <div className="absolute top-4 right-4 flex items-center gap-1.5 text-xs font-black px-3 py-1.5 rounded-full"
                style={{ background: D.gold, color: "#000" }}>
                <Download className="w-3 h-3" /> Téléchargement immédiat
              </div>
            </div>

            {/* Floating card 1 */}
            <motion.div className="absolute -left-4 top-1/3 rounded-xl p-3 hidden lg:flex items-center gap-2"
              style={{ background: "rgba(5,5,5,0.95)", border: `1px solid ${D.cardBorderOrange}`, boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}
              animate={{ y: [0,-10,0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ background: "rgba(255,122,0,0.15)" }}>📄</div>
              <div>
                <div className="text-xs font-bold" style={{ color: D.orange }}>+100 Modèles CV</div>
                <div className="text-xs" style={{ color: D.textMuted }}>Tous secteurs</div>
              </div>
            </motion.div>

            {/* Floating card 2 */}
            <motion.div className="absolute -right-4 bottom-1/4 rounded-xl p-3 hidden lg:flex items-center gap-2"
              style={{ background: "rgba(5,5,5,0.95)", border: `1px solid ${D.cardBorderGold}`, boxShadow: "0 8px 32px rgba(0,0,0,0.6)" }}
              animate={{ y: [0,10,0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm" style={{ background: "rgba(244,180,0,0.15)" }}>✅</div>
              <div>
                <div className="text-xs font-bold" style={{ color: D.gold }}>Compatible ATS</div>
                <div className="text-xs" style={{ color: D.textMuted }}>Certifié recruteurs</div>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats bar */}
        <motion.div className="mt-8 sm:mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {[
            { num: "+100", label: "Modèles de CV premium" },
            { num: "+90",  label: "Lettres de motivation" },
            { num: "ATS",  label: "Compatible recruteurs" },
            { num: "4.9/5",label: "Note des utilisateurs" },
          ].map(s => (
            <motion.div key={s.num} variants={fadeUp}
              className="text-center rounded-2xl py-5 px-3"
              style={{ background: D.card, border: `1px solid ${D.cardBorderGold}`, boxShadow: "0 0 20px rgba(244,180,0,0.05)" }}>
              <div className="text-2xl sm:text-3xl font-black" style={{ color: D.orange }}>{s.num}</div>
              <div className="text-sm mt-1" style={{ color: D.textMuted }}>{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   3. PROBLEM SECTION
═══════════════════════════════════════════════════════════ */
function ProblemSection() {
  return (
    <section className="py-20 px-4" style={{ background: D.bg2 }}>
      <div className="max-w-5xl mx-auto">
        <motion.div className="text-center mb-14"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5"
            style={{ background: "rgba(255,122,0,0.12)", color: D.orange, border: `1px solid ${D.cardBorderOrange}` }}>
            <AlertCircle className="w-4 h-4" /> Le problème réel
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black mb-4" style={{ color: D.textPrimary }}>
            Pourquoi votre candidature est<br className="hidden sm:block" /><span style={{ color: D.orange }}>ignorée ou rejetée ?</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg max-w-2xl mx-auto" style={{ color: D.textSecondary }}>
            Ce n'est pas un manque de talent. C'est un problème de <strong className="text-white">présentation, de stratégie et d'optimisation</strong>.
          </motion.p>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {[
            { pct: "75%",  desc: "des CV éliminés par les filtres ATS avant d'être lus" },
            { pct: "6 sec",desc: "temps moyen qu'un recruteur consacre à chaque CV" },
            { pct: "250+", desc: "candidatures en moyenne par offre publiée en ligne" },
            { pct: "80%",  desc: "des candidats n'adaptent jamais leur CV au poste visé" },
          ].map(s => (
            <motion.div key={s.pct} variants={fadeUp}
              whileHover={{ y:-4, boxShadow: `0 20px 50px rgba(255,122,0,0.12)` }}
              className="rounded-2xl p-6 text-center transition-all"
              style={{ background: D.card, border: `1px solid ${D.cardBorderOrange}` }}>
              <div className="text-4xl font-black mb-2" style={{ color: D.orange }}>{s.pct}</div>
              <p className="text-sm leading-relaxed" style={{ color: D.textSecondary }}>{s.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div className="rounded-3xl p-8 border"
          initial={{ opacity:0, y:30 }} whileInView={{ opacity:1, y:0 }} viewport={{ once: true }}
          style={{ background: "rgba(220,38,38,0.05)", borderColor: "rgba(220,38,38,0.2)" }}>
          <h3 className="text-xl font-bold mb-6 text-center text-white">Vous reconnaissez-vous dans ces situations ?</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Mon CV ne reçoit aucune réponse malgré mes efforts",
              "Je ne sais pas comment adapter ma lettre de motivation",
              "Mon profil est bloqué par les filtres ATS automatiques",
              "Je ne sais pas comment me démarquer des autres candidats",
              "Je manque de modèles professionnels prêts à utiliser",
              "Je n'ai aucun script LinkedIn pour contacter les recruteurs",
              "Je ne me sens pas préparé pour les entretiens",
              "Je perds des opportunités faute d'une bonne candidature",
            ].map(p => (
              <div key={p} className="flex items-start gap-3">
                <XCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
                <span className="text-sm" style={{ color: D.textSecondary }}>{p}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   4. SOLUTION SECTION
═══════════════════════════════════════════════════════════ */
function SolutionSection() {
  return (
    <section className="py-20 px-4 relative overflow-hidden" style={{ background: D.bg1 }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] blur-3xl pointer-events-none" style={{ background: "rgba(255,122,0,0.07)" }} />
      <div className="max-w-5xl mx-auto text-center relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-6"
            style={{ background: "rgba(255,122,0,0.14)", color: D.orange, border: `1px solid ${D.cardBorderOrange}` }}>
            <Sparkles className="w-4 h-4" /> La solution est ici
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-black mb-6 leading-tight" style={{ color: D.textPrimary, fontSize: "clamp(1.75rem, 5vw, 3rem)" }}>
            🎯 Un seul objectif :<br className="hidden sm:block" />
            <span style={{ color: D.orange }}>Rendre votre profil impossible à ignorer</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-xl max-w-3xl mx-auto mb-8 leading-relaxed" style={{ color: D.textSecondary }}>
            <strong className="text-white">CANDIDAT INCONTOURNABLE™</strong> regroupe en un seul pack tout ce dont vous avez besoin pour créer une candidature professionnelle, crédible et optimisée ATS —{" "}
            <span style={{ color: D.orange }}>sans expérience ni compétences techniques requises.</span>
          </motion.p>
          <motion.div variants={fadeUp} className="grid sm:grid-cols-3 gap-5 mt-10">
            {[
              { icon: "⚡", title: "Rapide", desc: "Candidature complète en moins de 30 minutes" },
              { icon: "🎯", title: "Stratégique", desc: "Modèles optimisés ATS + scripts recruteurs" },
              { icon: "💼", title: "Professionnel", desc: "Résultat digne des grandes entreprises" },
            ].map(f => (
              <motion.div key={f.title} variants={fadeUp}
                className="rounded-2xl p-6" style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
                <div className="text-4xl mb-3">{f.icon}</div>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm" style={{ color: D.textSecondary }}>{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} className="mt-10">
            <CTAButton label="🚀 JE VEUX CE PACK" className="w-full sm:w-auto text-xl px-12 py-5" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   5. PACK CONTENT
═══════════════════════════════════════════════════════════ */
function PackContentSection() {
  const items = [
    { icon:"📄", title:"+100 Modèles de CV",        desc:"Designs professionnels pour tous les secteurs, optimisés recruteurs", img:IMGS.cvguide, badge:"+100 modèles" },
    { icon:"✉️", title:"+90 Lettres de motivation", desc:"Lettres prêtes à personnaliser pour toutes les situations pro",         img:IMGS.letters, badge:"+90 lettres" },
    { icon:"📧", title:"Modèles de mails",           desc:"Scripts de candidature, relance et remerciement clé en main",          img:null,          badge:"Prêts à envoyer" },
    { icon:"🎤", title:"Guides entretien",           desc:"Méthode STAR, questions fréquentes, réponses types pour briller",      img:null,          badge:"Questions + Réponses" },
    { icon:"🤖", title:"Outils compatibles ATS",    desc:"Checklist complète pour passer les filtres automatiques",               img:null,          badge:"Anti-rejet ATS" },
    { icon:"💼", title:"Guides recherche d'emploi", desc:"Stratégies éprouvées pour trouver les meilleures opportunités",         img:null,          badge:"Stratégie complète" },
    { icon:"🔗", title:"Scripts LinkedIn",          desc:"Messages clé en main pour contacter recruteurs et décideurs",           img:null,          badge:"Réseau pro" },
    { icon:"✅", title:"Checklist ATS",             desc:"Vérification point par point pour maximiser vos chances",               img:null,          badge:"Validation pro" },
    { icon:"💬", title:"Groupe WhatsApp privé",     desc:"Accès à un espace privé de partage de ressources entre candidats",     img:null,          badge:"🎁 BONUS" },
  ];
  return (
    <section className="py-20 px-4" style={{ background: D.bg2 }}>
      <div className="max-w-6xl mx-auto">
        <motion.div className="text-center mb-14"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5"
            style={{ background: "rgba(244,180,0,0.12)", color: D.gold, border: `1px solid ${D.cardBorderGold}` }}>
            <Trophy className="w-4 h-4" /> Ce que vous obtenez
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black mb-4 text-white">Un pack complet &amp; professionnel</motion.h2>
          <motion.p variants={fadeUp} className="text-lg max-w-2xl mx-auto" style={{ color: D.textSecondary }}>
            Toutes les ressources pour créer une candidature qui convertit — réunies en un seul endroit.
          </motion.p>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {items.map(item => (
            <motion.div key={item.title} variants={fadeUp}
              whileHover={{ y:-4, boxShadow:`0 20px 50px rgba(244,180,0,0.1)` }}
              className="rounded-2xl overflow-hidden transition-all"
              style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
              {item.img && (
                <div className="relative h-36 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover object-top opacity-80" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom,transparent 40%,${D.bg3})` }} />
                  <span className="absolute top-3 right-3 text-xs font-black px-2.5 py-1 rounded-full" style={{ background: D.gold, color: "#000" }}>{item.badge}</span>
                </div>
              )}
              <div className="p-5">
                {!item.img && (
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-3xl">{item.icon}</span>
                    <span className="text-xs font-black px-2.5 py-1 rounded-full"
                      style={{ background: item.badge === "🎁 BONUS" ? D.gold : "rgba(255,122,0,0.12)", color: item.badge === "🎁 BONUS" ? "#000" : D.orange }}>
                      {item.badge}
                    </span>
                  </div>
                )}
                <h3 className="font-bold text-base mb-1 text-white">{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: D.textSecondary }}>{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Screenshots */}
        <motion.div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {[IMGS.overview, IMGS.folders, IMGS.letters2].map((src, i) => (
            <motion.div key={i} variants={fadeUp}
              className="rounded-2xl overflow-hidden" whileHover={{ scale:1.03 }}
              style={{ border: `1px solid ${D.cardBorderGold}`, boxShadow:`0 8px 32px rgba(0,0,0,0.5)` }}>
              <img src={src} alt={`Aperçu ${i+1}`} className="w-full h-40 object-cover object-top opacity-90" />
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center mt-10">
          <CTAButton label="⚡ ACCÈS IMMÉDIAT AU PACK" className="w-full sm:w-auto text-xl px-10 py-5" />
          <p className="text-sm mt-3" style={{ color: D.textMuted }}>📩 Accès livré instantanément après paiement</p>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   6. BENEFITS — AVANT / APRÈS
═══════════════════════════════════════════════════════════ */
function BenefitsSection() {
  const before = ["CV ignoré par les recruteurs","Lettre de motivation générique","Profil bloqué par les filtres ATS","Aucun réseau LinkedIn actif","Stress et manque de confiance","Opportunités perdues","Semaines sans réponse","Candidature amateur"];
  const after  = ["CV professionnel qui attire l'œil","Lettre percutante et personnalisée","Profil 100% compatible ATS","Scripts LinkedIn clé en main","Confiance et préparation complète","Plus d'entretiens décrochés","Réponses rapides des recruteurs","Image professionnelle crédible"];
  return (
    <section className="py-20 px-4" style={{ background: D.bg3 }}>
      <div className="max-w-5xl mx-auto">
        <motion.div className="text-center mb-14"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5"
            style={{ background: "rgba(255,122,0,0.12)", color: D.orange, border: `1px solid ${D.cardBorderOrange}` }}>
            <ArrowRight className="w-4 h-4" /> La transformation
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-white">
            Votre vie <span style={{ color: D.orange }}>avant et après</span> le pack
          </motion.h2>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-6"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {/* Before */}
          <motion.div variants={fadeUp} className="rounded-3xl p-8"
            style={{ background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.2)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(220,38,38,0.15)" }}>
                <XCircle className="w-5 h-5" style={{ color: "#ef4444" }} />
              </div>
              <h3 className="text-xl font-black text-white">AVANT le pack</h3>
            </div>
            <ul className="space-y-3">
              {before.map(b => (
                <li key={b} className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
                  <span className="text-sm" style={{ color: D.textSecondary }}>{b}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* After */}
          <motion.div variants={fadeUp} className="rounded-3xl p-8"
            style={{ background: "rgba(34,197,94,0.07)", border: "1px solid rgba(34,197,94,0.25)" }}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(34,197,94,0.15)" }}>
                <CheckCircle2 className="w-5 h-5" style={{ color: "#22c55e" }} />
              </div>
              <h3 className="text-xl font-black text-white">APRÈS le pack ✅</h3>
            </div>
            <ul className="space-y-3">
              {after.map(a => (
                <li key={a} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "#22c55e" }} />
                  <span className="text-sm font-medium" style={{ color: D.textSecondary }}>{a}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>

        <motion.div className="text-center mt-10"
          initial={{ opacity:0,scale:0.9 }} whileInView={{ opacity:1,scale:1 }} viewport={{ once:true }}>
          <div className="inline-block rounded-2xl px-8 py-4 text-white font-bold text-base"
            style={{ background: `linear-gradient(135deg,${D.bg1},${D.bg2})`, border: `1px solid ${D.cardBorderOrange}` }}>
            🚀 CANDIDAT INCONTOURNABLE™ = La différence entre être ignoré et être appelé
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   7. TESTIMONIALS
═══════════════════════════════════════════════════════════ */
function TestimonialsSection() {
  const testimonials = [
    { name:"Aminata D.", role:"Étudiante en Master — Dakar",       avatar:"AD", text:"Mon CV était ignoré avant. Après avoir utilisé les modèles du pack, j'ai commencé à recevoir des réponses rapidement. J'ai décroché un stage en moins de 3 semaines !", stars:5, hl:"Stage décroché en 3 semaines" },
    { name:"Kofi M.",    role:"Candidat emploi — Abidjan",         avatar:"KM", text:"Les lettres de motivation et les modèles ATS m'ont énormément aidé. Tout est très professionnel et facile à utiliser. Je recommande vraiment.",              stars:5, hl:"Profil optimisé ATS" },
    { name:"Fatou B.",   role:"Candidate bourse — Lomé",            avatar:"FB", text:"Les scripts LinkedIn m'ont permis de contacter directement des recruteurs. J'ai reçu 4 réponses positives en 2 semaines. Excellent investissement !",       stars:5, hl:"4 réponses en 2 semaines" },
    { name:"Ibrahim S.", role:"Ingénieur en reconversion — Bamako", avatar:"IS", text:"Pour 3 000 FCFA, c'est une vraie mine d'or. Les guides entretien m'ont aidé à me sentir beaucoup plus confiant. J'ai eu le poste !",                      stars:5, hl:"Poste obtenu !" },
    { name:"Aïchatou N.",role:"Fraîche diplômée — Ouagadougou",    avatar:"AN", text:"Je ne savais pas par où commencer. Avec ce pack, j'ai eu tout ce dont j'avais besoin. Les modèles sont modernes et professionnels.",                       stars:5, hl:"Démarrage facilité" },
    { name:"Marcel K.",  role:"Commercial — Cotonou",               avatar:"MK", text:"Le groupe WhatsApp est vraiment un plus pour partager les ressources. Ce pack vaut bien plus que son prix.",                                                 stars:5, hl:"Ressources partagées" },
  ];
  return (
    <section className="py-20 px-4" style={{ background: D.bg1 }}>
      <div className="max-w-6xl mx-auto">
        <motion.div className="text-center mb-14"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5"
            style={{ background: "rgba(244,180,0,0.12)", color: D.gold, border: `1px solid ${D.cardBorderGold}` }}>
            <Star className="w-4 h-4 fill-current" /> Ce que disent nos clients
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-white mb-4">
            Pourquoi les candidats choisissent<br /><span style={{ color: D.orange }}>CANDIDAT INCONTOURNABLE™</span>
          </motion.h2>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {testimonials.map(t => (
            <motion.div key={t.name} variants={fadeUp}
              whileHover={{ y:-4, boxShadow:`0 20px 50px rgba(244,180,0,0.1)` }}
              className="rounded-2xl p-6 transition-all"
              style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
              <div className="flex gap-0.5 mb-3">
                {Array.from({ length: t.stars }).map((_,i) => <Star key={i} className="w-4 h-4 fill-current" style={{ color: D.gold }} />)}
              </div>
              <p className="text-sm leading-relaxed mb-4 italic" style={{ color: D.textSecondary }}>"{t.text}"</p>
              <div className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full mb-4"
                style={{ background: "rgba(255,122,0,0.12)", color: D.orange }}>
                <Check className="w-3 h-3" /> {t.hl}
              </div>
              <div className="flex items-center gap-3 pt-3" style={{ borderTop: `1px solid ${D.divider}` }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-black shrink-0 text-white"
                  style={{ background: `linear-gradient(135deg,${D.orange},${D.gold})` }}>{t.avatar}</div>
                <div>
                  <div className="text-sm font-semibold text-white">{t.name}</div>
                  <div className="text-xs" style={{ color: D.textMuted }}>{t.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Social stats */}
        <motion.div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {[
            { icon:<Download className="w-6 h-6" />, num:"+500",  label:"Téléchargements" },
            { icon:<Users className="w-6 h-6" />,    num:"+300",  label:"Profils transformés" },
            { icon:<Star className="w-6 h-6 fill-current" />, num:"4.9/5", label:"Note moyenne" },
            { icon:<Trophy className="w-6 h-6" />,  num:"3 domaines", label:"Emplois · Stages · Bourses" },
          ].map(s => (
            <motion.div key={s.num} variants={fadeUp}
              className="text-center rounded-2xl p-5"
              style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
              <div className="flex justify-center mb-2" style={{ color: D.gold }}>{s.icon}</div>
              <div className="text-xl font-black text-white">{s.num}</div>
              <div className="text-xs mt-1" style={{ color: D.textMuted }}>{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   8. BONUS SECTION
═══════════════════════════════════════════════════════════ */
function BonusSection() {
  const bonuses = [
    { icon:"💬", title:"Groupe WhatsApp Privé",         desc:"Espace privé de partage de ressources et d'entraide entre candidats sérieux",           value:"10 000 FCFA" },
    { icon:"🔗", title:"Scripts LinkedIn Clé en Main",  desc:"Messages prêts à envoyer pour approcher recruteurs et décideurs sur LinkedIn",          value:"8 000 FCFA" },
    { icon:"✅", title:"Checklist ATS Complète",        desc:"Vérification point par point pour que votre CV passe les filtres automatiques",          value:"5 000 FCFA" },
    { icon:"🎤", title:"Questions d'entretien + Réponses", desc:"Plus de 50 questions fréquentes avec les meilleures réponses type à personnaliser",    value:"7 000 FCFA" },
    { icon:"📚", title:"Guides Supplémentaires",        desc:"Stratégies avancées pour booster votre recherche d'emploi et élargir votre réseau",      value:"6 000 FCFA" },
  ];
  return (
    <section className="py-20 px-4" style={{ background: D.bg2 }}>
      <div className="max-w-5xl mx-auto">
        <motion.div className="text-center mb-14"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-black px-5 py-2 rounded-full mb-5"
            style={{ background: D.gold, color: "#000" }}>
            🎁 BONUS OFFERTS AVEC LE PACK
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-white mb-4">
            Ces bonus sont inclus <span style={{ color: D.orange }}>gratuitement</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg" style={{ color: D.textSecondary }}>
            Valeur totale des bonus : <strong style={{ color: D.orange }}>36 000 FCFA offerts</strong>
          </motion.p>
        </motion.div>

        <motion.div className="grid sm:grid-cols-2 gap-5"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {bonuses.map(b => (
            <motion.div key={b.title} variants={fadeUp}
              whileHover={{ y:-3, boxShadow:`0 16px 40px rgba(244,180,0,0.12)` }}
              className="rounded-2xl p-6 flex gap-4 items-start transition-all"
              style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
              <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl shrink-0"
                style={{ background: `linear-gradient(135deg,rgba(244,180,0,0.2),rgba(255,122,0,0.15))`, border: `1px solid ${D.cardBorderGold}` }}>
                {b.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-bold text-base text-white">{b.title}</h3>
                  <span className="text-xs font-black px-2 py-1 rounded-full whitespace-nowrap shrink-0"
                    style={{ background: "rgba(244,180,0,0.12)", color: D.gold }}>
                    {b.value}
                  </span>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: D.textSecondary }}>{b.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Total value card */}
        <motion.div className="mt-10 rounded-3xl p-8 text-center relative overflow-hidden"
          initial={{ opacity:0,y:30 }} whileInView={{ opacity:1,y:0 }} viewport={{ once:true }}
          style={{ background: D.card, border: `1px solid ${D.cardBorderGold}`, boxShadow:`0 0 60px rgba(244,180,0,0.08)` }}>
          <div className="absolute inset-0 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle at 50% 50%,rgba(244,180,0,0.06),transparent 70%)" }} />
          <p className="text-sm mb-2 relative z-10" style={{ color: D.textMuted }}>Valeur totale si acheté séparément</p>
          <p className="text-4xl font-black text-white line-through opacity-50 mb-2 relative z-10">55 000 FCFA</p>
          <p className="text-sm mb-4 relative z-10" style={{ color: D.textMuted }}>Votre prix aujourd'hui :</p>
          <p className="font-black mb-1 relative z-10" style={{ fontSize:"clamp(3.5rem,15vw,5rem)",color:D.orange,lineHeight:1,textShadow:`0 0 50px rgba(255,122,0,0.6)` }}>3 000 FCFA</p>
          <p className="mb-6 relative z-10" style={{ color:D.textMuted }}>≈ 5 USD · Paiement unique · Accès à vie</p>
          <div className="relative z-10"><CTAButton label="🔥 OBTENIR LE PACK MAINTENANT" className="w-full sm:w-auto text-xl px-10 py-5" /></div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   9. WHY EXCEPTIONAL
═══════════════════════════════════════════════════════════ */
function WhyExceptionalSection() {
  return (
    <section className="py-16 px-4" style={{ background: D.bg3 }}>
      <div className="max-w-4xl mx-auto">
        <motion.div className="rounded-3xl overflow-hidden"
          initial={{ opacity:0,y:30 }} whileInView={{ opacity:1,y:0 }} viewport={{ once:true }}
          style={{ background: D.card, border: `1px solid ${D.cardBorderOrange}` }}>
          <div className="p-8 sm:p-10 text-center">
            <div className="inline-flex items-center gap-2 text-sm font-black px-5 py-2 rounded-full mb-6"
              style={{ background: "rgba(255,122,0,0.12)", color: D.orange, border: `1px solid ${D.cardBorderOrange}` }}>
              <Sparkles className="w-4 h-4" /> Pourquoi cette offre est exceptionnelle ?
            </div>
            <h2 className="text-3xl font-black text-white mb-6">
              Le pack regroupe des ressources qui coûteraient{" "}
              <span style={{ color: D.orange }}>bien plus cher séparément</span>
            </h2>
            <div className="grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto">
              {[
                { item:"Pack 100 modèles CV complets",          prix:"15 000 FCFA" },
                { item:"Pack 90 lettres de motivation",         prix:"12 000 FCFA" },
                { item:"Guide entretien + scripts LinkedIn",    prix:"8 000 FCFA" },
                { item:"Espace WhatsApp + ressources privées",  prix:"10 000 FCFA" },
                { item:"Tous les guides + checklists",          prix:"10 000 FCFA" },
                { item:"Modèles de mails professionnels",       prix:"5 000 FCFA" },
              ].map(r => (
                <div key={r.item} className="flex items-center justify-between p-3 rounded-xl"
                  style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${D.divider}` }}>
                  <span className="text-sm font-medium" style={{ color: D.textSecondary }}>{r.item}</span>
                  <span className="text-sm font-black" style={{ color: D.orange }}>{r.prix}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 rounded-2xl" style={{ background: "rgba(255,122,0,0.08)", border: `1px dashed rgba(255,122,0,0.3)` }}>
              <p className="font-bold text-white">Total séparément : <span className="line-through" style={{ color:D.textMuted }}>55 000 FCFA</span></p>
              <p className="text-3xl font-black mt-1" style={{ color: D.orange }}>Avec le pack : 3 000 FCFA seulement !</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   10. TRUST & CREDIBILITY
═══════════════════════════════════════════════════════════ */
function TrustSection() {
  const trust = [
    { icon:<Zap className="w-6 h-6" />,        title:"Accès immédiat",     desc:"Livraison instantanée après paiement" },
    { icon:<Smartphone className="w-6 h-6" />,  title:"Compatible mobile",  desc:"Smartphone, tablette & PC" },
    { icon:<FileText className="w-6 h-6" />,    title:"Format Word & PDF",  desc:"Faciles à modifier et personnaliser" },
    { icon:<Globe className="w-6 h-6" />,       title:"Tous niveaux",       desc:"Débutants & professionnels" },
    { icon:<Download className="w-6 h-6" />,    title:"Accès à vie",        desc:"Disponible dès l'achat, pour toujours" },
    { icon:<Shield className="w-6 h-6" />,      title:"Paiement sécurisé",  desc:"Transaction 100% protégée" },
  ];
  return (
    <section className="py-20 px-4" style={{ background: D.bg2 }}>
      <div className="max-w-5xl mx-auto">
        <motion.div className="text-center mb-12"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5"
            style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}>
            <Shield className="w-4 h-4" /> Sécurité &amp; Confiance
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-white">
            Un produit 100% fiable et professionnel
          </motion.h2>
        </motion.div>
        <motion.div className="grid grid-cols-2 sm:grid-cols-3 gap-4"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          {trust.map(t => (
            <motion.div key={t.title} variants={fadeUp}
              className="flex flex-col items-center text-center p-5 rounded-2xl transition-all"
              whileHover={{ y:-3, boxShadow:"0 16px 40px rgba(34,197,94,0.1)" }}
              style={{ background: D.card, border: "1px solid rgba(34,197,94,0.2)" }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
                style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>{t.icon}</div>
              <h3 className="font-bold text-sm mb-1 text-white">{t.title}</h3>
              <p className="text-xs" style={{ color: D.textMuted }}>{t.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   11. PRICE SECTION
═══════════════════════════════════════════════════════════ */
function PriceSection() {
  const { h, m, s } = useCountdown();
  return (
    <section className="py-20 px-4 relative overflow-hidden" style={{ background: D.bg1 }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 50%,rgba(255,122,0,0.07),transparent 70%)" }} />
      <div className="max-w-2xl mx-auto text-center relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-black px-5 py-2 rounded-full mb-6 animate-pulse"
            style={{ background: D.orange, color: "#fff" }}>
            🔥 OFFRE EXCEPTIONNELLE — DURÉE LIMITÉE
          </motion.div>
          <motion.h2 variants={fadeUp} className="font-black text-white mb-6" style={{ fontSize: "clamp(1.6rem, 5vw, 2.5rem)" }}>
            Obtenez <span style={{ color: D.orange }}>CANDIDAT INCONTOURNABLE™</span><br className="hidden sm:block" />au prix le plus bas jamais proposé
          </motion.h2>

          {/* Price card */}
          <motion.div variants={fadeUp} className="rounded-3xl p-8 mb-8 relative overflow-hidden"
            style={{ background: D.card, border: `2px solid ${D.cardBorderOrange}`, boxShadow:`0 0 60px rgba(255,122,0,0.1)` }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 0%,rgba(255,122,0,0.08),transparent 60%)" }} />
            <div className="relative z-10">
              <p className="text-sm mb-2" style={{ color: D.textMuted }}>Prix original</p>
              <p className="text-4xl font-black line-through mb-3 font-mono" style={{ color: "#4b5563" }}>55 000 FCFA</p>
              <div className="inline-flex items-center gap-2 text-sm font-black px-4 py-2 rounded-full mb-4"
                style={{ background: "rgba(244,180,0,0.15)", color: D.gold }}>
                🏷️ ÉCONOMISEZ 52 000 FCFA — 95% de réduction
              </div>
              <p className="text-sm mb-1" style={{ color: D.textMuted }}>Votre prix aujourd'hui :</p>
              <p className="font-black mb-1" style={{ fontSize:"clamp(3.5rem,15vw,5rem)",color:D.orange,lineHeight:1,textShadow:`0 0 50px rgba(255,122,0,0.6)` }}>3 000 FCFA</p>
              <p className="text-lg mb-6" style={{ color: D.textMuted }}>≈ 5 USD · Paiement unique</p>
              <div className="grid grid-cols-2 gap-2 mb-6 text-left">
                {["✅ +100 Modèles CV","✅ +90 Lettres motivation","✅ Outils ATS","✅ Guides entretien","✅ Scripts LinkedIn","✅ Groupe WhatsApp","✅ Checklist ATS","✅ Accès à vie"].map(f => (
                  <div key={f} className="text-sm" style={{ color: D.textSecondary }}>{f}</div>
                ))}
              </div>
              <CTAButton label="🎯 JE COMMENCE MAINTENANT" className="w-full text-xl py-5" />
            </div>
          </motion.div>

          {/* Countdown */}
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 flex-wrap">
            <Clock className="w-5 h-5" style={{ color: D.orange }} />
            <span className="text-sm" style={{ color: D.textSecondary }}>Le prix revient à 55 000 FCFA dans :</span>
            {[{v:h,l:"H"},{v:m,l:"M"},{v:s,l:"S"}].map((t,i) => (
              <span key={i} className="flex flex-col items-center">
                <span className="font-mono font-black text-2xl w-14 text-center rounded-xl py-2"
                  style={{ background: "rgba(255,122,0,0.12)", color: D.orange, textShadow:`0 0 20px rgba(255,122,0,0.5)` }}>
                  {pad(t.v)}
                </span>
                <span className="text-xs mt-1" style={{ color: D.textMuted }}>{t.l}</span>
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   12. PAYMENT SECTION
═══════════════════════════════════════════════════════════ */
function PaymentSection() {
  const methods = [
    { icon:"📱", name:"Mobile Money",    detail:"Toutes opérations" },
    { icon:"🟠", name:"Orange Money",   detail:"Paiement instantané" },
    { icon:"🟡", name:"MTN Mobile Money",detail:"Disponible partout" },
    { icon:"🔵", name:"Moov Money",     detail:"Rapide & sécurisé" },
    { icon:"💳", name:"Carte bancaire", detail:"Visa / Mastercard" },
    { icon:"🌐", name:"Paiement en ligne",detail:"Sécurisé & chiffré" },
  ];
  return (
    <section className="py-20 px-4" style={{ background: D.bg3 }}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-6"
            style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}>
            <Lock className="w-4 h-4" /> Paiement 100% sécurisé
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl font-black text-white mb-3">
            Commandez en <span style={{ color: D.orange }}>moins de 2 minutes</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg mb-10" style={{ color: D.textSecondary }}>
            Tous les moyens de paiement acceptés. Validation instantanée. Accès immédiat.
          </motion.p>
          <motion.div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10" variants={stagger}>
            {methods.map(p => (
              <motion.div key={p.name} variants={fadeUp}
                whileHover={{ y:-3, boxShadow:`0 16px 36px rgba(244,180,0,0.1)` }}
                className="rounded-2xl p-5 flex flex-col items-center gap-2 transition-all"
                style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
                <span className="text-3xl">{p.icon}</span>
                <span className="font-bold text-sm text-white">{p.name}</span>
                <span className="text-xs" style={{ color: D.textMuted }}>{p.detail}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Security badges */}
          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-3 mb-10">
            {[
              { icon:<Lock className="w-4 h-4" />, text:"Paiement sécurisé", c:"#22c55e" },
              { icon:<Zap className="w-4 h-4" />,  text:"Validation instantanée", c:D.orange },
              { icon:<MessageCircle className="w-4 h-4" />, text:"Accès immédiat", c:D.gold },
              { icon:<Award className="w-4 h-4" />, text:"Accès à vie garanti", c:D.gold },
            ].map(b => (
              <div key={b.text} className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full"
                style={{ background: "rgba(255,255,255,0.05)", color: b.c, border: `1px solid rgba(255,255,255,0.08)` }}>
                {b.icon} {b.text}
              </div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp}>
            <CTAButton label="🚀 OBTENIR LE PACK MAINTENANT" className="w-full sm:w-auto text-xl px-10 py-5" />
            <p className="mt-3 text-sm" style={{ color: D.textMuted }}>
              Commandez en moins de 2 minutes et recevez immédiatement votre accès.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   13. FAQ
═══════════════════════════════════════════════════════════ */
function FAQSection() {
  const faqs = [
    { q:"Comment est-ce que j'accède au pack après le paiement ?", a:"Immédiatement après votre paiement, vous recevez un lien de téléchargement. L'accès est automatique et instantané, 24h/24." },
    { q:"Quel est le format des fichiers ?", a:"Les modèles CV et lettres sont au format Word (.docx) et PDF, modifiables avec Microsoft Word, Google Docs ou LibreOffice. Les guides sont en PDF." },
    { q:"Combien de temps l'accès dure-t-il ?", a:"L'accès est à vie. Une fois acheté, le pack vous appartient pour toujours. Vous pouvez le télécharger autant de fois que vous le souhaitez." },
    { q:"Est-ce compatible avec mon téléphone mobile ?", a:"Oui, tous les fichiers sont optimisés pour mobile, tablette et PC. Vous pouvez ouvrir, modifier et partager depuis votre smartphone." },
    { q:"Ce pack convient-il aux débutants ?", a:"Absolument ! Le pack est conçu pour tout le monde — premier emploi, reconversion ou recherche de stage. Les modèles sont prêts à remplir, aucune compétence technique requise." },
    { q:"Quels moyens de paiement sont acceptés ?", a:"Nous acceptons Mobile Money (Orange Money, MTN, Moov), les cartes bancaires Visa/Mastercard et le paiement en ligne sécurisé. Simple et rapide depuis mobile." },
    { q:"Puis-je télécharger le pack plusieurs fois ?", a:"Oui ! Une fois acheté, vous pouvez télécharger le pack autant de fois que nécessaire. L'accès est permanent et illimité." },
  ];
  return (
    <section className="py-20 px-4" style={{ background: D.bg2 }}>
      <div className="max-w-3xl mx-auto">
        <motion.div className="text-center mb-12"
          initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}
            className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full mb-5"
            style={{ background: "rgba(255,122,0,0.12)", color: D.orange, border: `1px solid ${D.cardBorderOrange}` }}>
            <ChevronDown className="w-4 h-4" /> Questions fréquentes
          </motion.div>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-black text-white">Toutes vos réponses ici</motion.h2>
        </motion.div>
        <motion.div initial={{ opacity:0,y:30 }} whileInView={{ opacity:1,y:0 }} viewport={{ once:true }} transition={{ duration:0.6 }}>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}
                className="rounded-2xl overflow-hidden px-6"
                style={{ background: D.card, border: `1px solid ${D.cardBorderGold}` }}>
                <AccordionTrigger
                  className="text-left font-semibold hover:no-underline py-5 text-sm sm:text-base"
                  style={{ color: D.textPrimary }}>
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed pb-5" style={{ color: D.textSecondary }}>
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   14. FINAL CTA
═══════════════════════════════════════════════════════════ */
function FinalCTASection() {
  return (
    <section className="py-24 px-4 relative overflow-hidden" style={{ background: D.bg1 }}>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] blur-3xl pointer-events-none" style={{ background: "radial-gradient(ellipse,rgba(255,122,0,0.18),transparent 70%)" }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg,transparent,${D.cardBorderGold},transparent)` }} />
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.p variants={fadeUp} className="text-6xl mb-6">🔥</motion.p>
          <motion.h2 variants={fadeUp} className="font-black text-white mb-6 leading-tight"
            style={{ fontSize: "clamp(1.75rem, 6vw, 3rem)" }}>
            Ne laissez plus des candidatures faibles<br className="hidden sm:block" />
            <span style={{ color: D.orange }}>fermer vos portes professionnelles.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-xl mb-4 leading-relaxed" style={{ color: D.textSecondary }}>
            🚀 Une meilleure candidature peut <strong className="text-white">changer toute votre carrière.</strong>
          </motion.p>
          <motion.p variants={fadeUp} className="mb-10 leading-relaxed" style={{ color: D.textMuted }}>
            Pour moins d'un repas, obtenez un pack de ressources professionnelles complet qui vous donnera tous les outils pour vous démarquer et décrocher les opportunités que vous méritez.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col items-center gap-4">
            <CTAButton label="🚀 JE PROFITE DE L'OFFRE MAINTENANT" className="text-xl sm:text-2xl px-10 py-6 w-full sm:w-auto" />
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              {[
                { icon:<Lock className="w-4 h-4" />, t:"Paiement sécurisé", c:"#22c55e" },
                { icon:<Zap className="w-4 h-4" />,  t:"Accès immédiat",    c:D.orange },
                { icon:<Award className="w-4 h-4" />, t:"Accès à vie",      c:D.gold },
              ].map(b => (
                <span key={b.t} className="flex items-center gap-1" style={{ color: D.textMuted }}>{b.icon} {b.t}</span>
              ))}
            </div>
            <div className="text-center">
              <p className="line-through text-lg font-mono" style={{ color: D.textMuted }}>55 000 FCFA</p>
              <p className="text-5xl font-black" style={{ color: D.orange }}>3 000 FCFA</p>
              <p className="text-sm" style={{ color: D.textMuted }}>≈ 5 USD · Une seule fois · Pour toujours</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   EMOTIONAL CLOSING SECTION
═══════════════════════════════════════════════════════════ */
function EmotionalClosingSection() {
  return (
    <section className="py-20 px-4 relative overflow-hidden" style={{ background: "linear-gradient(160deg,#060606 0%,#0d0d0d 50%,#060606 100%)" }}>
      {/* Subtle glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] blur-3xl pointer-events-none opacity-20"
        style={{ background: "radial-gradient(ellipse,#FF7A00,transparent 70%)" }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg,transparent,rgba(244,180,0,0.3),transparent)" }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg,transparent,rgba(244,180,0,0.15),transparent)" }} />

      <div className="max-w-2xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Top badge */}
          <div className="inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full mb-8"
            style={{ background: "rgba(255,122,0,0.1)", border: "1px solid rgba(255,122,0,0.25)", color: "#FF7A00" }}>
            ✦ Message important pour vous
          </div>

          {/* Main emotional text */}
          <div className="space-y-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.15 }}
              className="text-2xl sm:text-3xl font-black leading-tight text-white"
              style={{ letterSpacing: "-0.02em" }}
            >
              🔥 ⏳{" "}
              <span style={{ background: "linear-gradient(90deg,#fff,#e2e8f0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Ne laissez plus des candidatures faibles
              </span>
              {" "}
              <span style={{
                background: "linear-gradient(90deg,#FF7A00,#FFB703)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: "drop-shadow(0 0 8px rgba(255,122,0,0.4))",
              }}>
                fermer vos portes professionnelles.
              </span>
            </motion.p>

            <motion.div
              initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.3 }}
              className="mx-auto h-px w-24"
              style={{ background: "linear-gradient(90deg,transparent,rgba(244,180,0,0.6),transparent)" }}
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.4 }}
              className="text-xl sm:text-2xl font-bold leading-relaxed"
              style={{ color: "#C9C9C9", letterSpacing: "-0.01em" }}
            >
              🚀{" "}
              <strong className="text-white">Une candidature professionnelle, moderne et convaincante</strong>
              {" "}
              <span style={{
                background: "linear-gradient(90deg,#F4B400,#FFC857)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>
                peut changer toute votre carrière.
              </span>
            </motion.p>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10"
          >
            <CTAButton label="🚀 JE PROFITE DE L'OFFRE — 3 000 FCFA" className="text-lg sm:text-xl px-8 py-5 w-full sm:w-auto" />
            <p className="mt-3 text-xs" style={{ color: "#555" }}>
              ⚡ Accès immédiat · Paiement unique · Accès à vie
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUPPORT & CONTACT SECTION
═══════════════════════════════════════════════════════════ */
function SupportSection() {
  const SUPPORT_EMAIL = "contact.edigitalgroup@gmail.com";

  const trustItems = [
    {
      icon: "🔒",
      label: "Paiement sécurisé",
      desc: "Transaction chiffrée et protégée",
      color: "#22c55e",
    },
    {
      icon: "⚡",
      label: "Accès immédiat",
      desc: "Débloquez le pack après validation",
      color: "#FF7A00",
    },
    {
      icon: "📩",
      label: "Livraison instantanée",
      desc: "Reçu directement dans votre email",
      color: "#3b82f6",
    },
    {
      icon: "✅",
      label: "Satisfaction garantie",
      desc: "Des milliers de professionnels font confiance",
      color: "#F4B400",
    },
  ];

  return (
    <section
      className="py-20 px-4 relative overflow-hidden"
      style={{ background: "linear-gradient(180deg,#050505 0%,#080c14 60%,#050505 100%)" }}
    >
      {/* Subtle top separator */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: "linear-gradient(90deg,transparent,rgba(59,130,246,0.25),rgba(255,122,0,0.2),transparent)" }}
      />

      {/* Ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] blur-3xl pointer-events-none opacity-10"
        style={{ background: "radial-gradient(ellipse,#3b82f6 0%,#FF7A00 50%,transparent 70%)" }}
      />

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14"
        >
          <div
            className="inline-flex items-center gap-2 text-xs font-bold px-4 py-1.5 rounded-full mb-5"
            style={{
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.2)",
              color: "#60a5fa",
            }}
          >
            💬 Support &amp; Assistance
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black text-white mb-3"
            style={{ letterSpacing: "-0.02em" }}
          >
            Nous sommes là pour vous
          </h2>
          <p className="text-sm sm:text-base max-w-lg mx-auto" style={{ color: "#8a99b3" }}>
            Une équipe professionnelle à votre disposition pour répondre à toutes vos questions.
          </p>
        </motion.div>

        {/* Trust badges row */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10"
        >
          {trustItems.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.08 * i }}
              className="flex flex-col items-center text-center p-4 rounded-2xl"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: `1px solid rgba(255,255,255,0.07)`,
                boxShadow: `0 0 24px ${item.color}12`,
              }}
            >
              <span className="text-2xl mb-2">{item.icon}</span>
              <div
                className="text-xs font-black mb-1 uppercase tracking-wide"
                style={{ color: item.color }}
              >
                {item.label}
              </div>
              <div className="text-xs leading-snug" style={{ color: "#6b7ea0" }}>
                {item.desc}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main support card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="rounded-3xl overflow-hidden"
          style={{
            background: "linear-gradient(145deg,rgba(255,255,255,0.04) 0%,rgba(255,255,255,0.015) 100%)",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 8px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
            backdropFilter: "blur(16px)",
          }}
        >
          {/* Card inner */}
          <div className="p-8 sm:p-12 flex flex-col sm:flex-row items-center sm:items-start gap-8">
            {/* Left: icon block */}
            <div className="flex-shrink-0 flex flex-col items-center gap-3">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
                style={{
                  background: "linear-gradient(135deg,rgba(59,130,246,0.15),rgba(255,122,0,0.1))",
                  border: "1px solid rgba(59,130,246,0.25)",
                  boxShadow: "0 0 32px rgba(59,130,246,0.2)",
                }}
              >
                📩
              </div>
              {/* Online indicator */}
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ background: "#22c55e", boxShadow: "0 0 8px #22c55e" }}
                />
                <span className="text-xs font-semibold" style={{ color: "#22c55e" }}>
                  En ligne
                </span>
              </div>
            </div>

            {/* Right: content */}
            <div className="flex-1 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
                <h3 className="text-lg font-black text-white">Support Client</h3>
                <span
                  className="text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)" }}
                >
                  Réponse rapide
                </span>
              </div>
              <p className="text-sm mb-4" style={{ color: "#6b7ea0" }}>
                💬 Assistance rapide et professionnelle — nous répondons sous 24h ouvrées.
              </p>

              {/* Email display */}
              <div
                className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-xl mb-6 font-mono text-sm w-full"
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: "#c8d6f0",
                }}
              >
                <span style={{ color: "#60a5fa" }}>📩</span>
                <span className="font-semibold" style={{ wordBreak: "break-all" }}>{SUPPORT_EMAIL}</span>
              </div>

              {/* CTA Button */}
              <div>
                <motion.a
                  href="mailto:contact.edigitalgroup@gmail.com?subject=Support%20CANDIDAT%20INCONTOURNABLE%E2%84%A2&body=Bonjour%2C%0A%0A"
                  whileHover={{ scale: 1.03, boxShadow: "0 0 32px rgba(59,130,246,0.45)" }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.22 }}
                  className="inline-flex items-center gap-2.5 font-black text-sm px-7 py-3.5 rounded-xl text-white"
                  style={{
                    background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
                    boxShadow: "0 4px 20px rgba(37,99,235,0.35), inset 0 1px 0 rgba(255,255,255,0.1)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    letterSpacing: "0.01em",
                    textDecoration: "none",
                  }}
                >
                  🚀 CONTACTER LE SUPPORT
                </motion.a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="px-8 sm:px-12 py-4 flex flex-wrap items-center justify-center sm:justify-between gap-3"
            style={{ borderTop: "1px solid rgba(255,255,255,0.05)", background: "rgba(0,0,0,0.2)" }}
          >
            {[
              { icon: "🔒", text: "Données protégées" },
              { icon: "⚡", text: "Réponse sous 24h" },
              { icon: "💬", text: "Support humain" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="text-sm">{item.icon}</span>
                <span className="text-xs font-semibold" style={{ color: "#4b5f7c" }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════════
   15. FOOTER
═══════════════════════════════════════════════════════════ */
function Footer() {
  return (
    <footer className="py-10 px-4" style={{ background: D.bg0, borderTop: `1px solid ${D.divider}` }}>
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <div className="font-black text-lg text-white mb-1">CANDIDAT INCONTOURNABLE™</div>
            <div className="text-sm" style={{ color: D.textMuted }}>Par Digital Energy Group</div>
          </div>
          <div className="flex flex-wrap justify-center sm:justify-end gap-4 text-sm" style={{ color: D.textMuted }}>
            <a href={CHECKOUT} className="hover:text-white transition-colors">Acheter maintenant</a>
            <span>·</span>
            <span>Paiement sécurisé</span>
            <span>·</span>
            <span>Accès à vie</span>
          </div>
        </div>
        <div className="mt-8 pt-6 text-center text-xs" style={{ borderTop: `1px solid ${D.divider}`, color: D.textMuted }}>
          © 2026 Digital Energy Group. Tous droits réservés. · CANDIDAT INCONTOURNABLE™ est un produit digital.
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════
   16. STICKY MOBILE CTA
═══════════════════════════════════════════════════════════ */
function StickyCTA() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const h = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:hidden"
          style={{ background: "rgba(3,3,3,0.97)", borderTop: `1px solid ${D.cardBorderOrange}` }}>
          <a href={CHECKOUT} target="_blank" rel="noopener noreferrer"
            className="flex items-center justify-between w-full rounded-2xl px-5 py-4 text-white font-black text-base"
            style={{ background: `linear-gradient(135deg,${D.orange},#FFB703)`, boxShadow:`0 4px 24px rgba(255,122,0,0.45)` }}>
            <span>🚀 JE PROFITE DE L'OFFRE</span>
            <span className="text-sm font-bold opacity-90">3 000 FCFA</span>
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */
export default function Index() {
  return (
    <>
      <style>{`
        @keyframes shimmerBg {
          0%   { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
      `}</style>
      <div style={{ background: "#050505", minHeight: "100vh" }}>
        <UrgencyBar />
        <NavBar />
        <HeroSection />
        <ProblemSection />
        <SolutionSection />
        <PackContentSection />
        <BenefitsSection />
        <TestimonialsSection />
        <BonusSection />
        <WhyExceptionalSection />
        <TrustSection />
        <PriceSection />
        <PaymentSection />
        <FAQSection />
        <FinalCTASection />
        <EmotionalClosingSection />
        <SupportSection />
        <Footer />
        <StickyCTA />
        <ConversionToast />
        <SalesPopup />
      </div>
    </>
  );
}
